import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

export default function AnthropicConfigPage() {
  const [anthropicApiKey, setAnthropicApiKey] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/me')
        setAnthropicApiKey(data.anthropic_api_key || '')
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Erreur chargement paramètres')
      }
      setLoading(false)
    })()
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      await api.put('/settings/me', { anthropic_api_key: anthropicApiKey })
      setMessage('Clé API Anthropic sauvegardée avec succès ✅')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur sauvegarde paramètres')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <ConfigPageLayout title="ANTHROPIC" description="Configuration de votre clé API Anthropic" backTo="/config">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="ANTHROPIC" description="Configuration de votre clé API Anthropic pour utiliser les modèles Claude dans vos agents" backTo="/config">
      <form onSubmit={submit} className="space-y-6">
        <section className="space-y-6 rounded-3xl border border-orange-100/80 bg-white/90 p-8 shadow-xl shadow-orange-500/10 dark:border-orange-900/50 dark:bg-zinc-950/90">
          <div className="flex items-center gap-3 pb-4 border-b border-orange-200/50 dark:border-orange-800/50">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 dark:bg-orange-900/40">
              <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-orange-900 dark:text-orange-100">Clé API Anthropic</h2>
              <p className="text-sm text-orange-700/70 dark:text-orange-300/70">Configuration optionnelle pour utiliser les modèles Claude</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">Anthropic API Key</label>
              <input 
                type="password" 
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 bg-white/80 dark:bg-zinc-950/70 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition" 
                value={anthropicApiKey} 
                onChange={e=>setAnthropicApiKey(e.target.value)} 
                placeholder="sk-ant-..." 
                disabled={saving}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Votre clé API Anthropic (commence par sk-ant-...)
              </p>
            </div>

            <div className="rounded-xl border border-orange-200/70 bg-orange-50/50 p-4 dark:border-orange-900/50 dark:bg-orange-900/20">
              <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-2">À quoi sert cette clé ?</h3>
              <ul className="space-y-2 text-xs text-orange-700 dark:text-orange-300">
                <li className="flex items-start gap-2">
                  <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Permet d'utiliser les modèles Claude (Claude 3.5 Sonnet, Claude 3 Opus, etc.) dans vos agents</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Accès aux dernières fonctionnalités d'Anthropic</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Facturation directe sur votre compte Anthropic</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-800/30">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-2">Comment obtenir une clé API ?</h3>
              <ol className="space-y-2 text-xs text-zinc-600 dark:text-zinc-400">
                <li className="flex items-start gap-2">
                  <span className="font-semibold">1.</span>
                  <span>Créez un compte sur <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline">console.anthropic.com</a></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">2.</span>
                  <span>Accédez à la section "API Keys" dans votre profil</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold">3.</span>
                  <span>Créez une nouvelle clé API et copiez-la ici</span>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <section className="space-y-4 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
              {error}
            </div>
          )}
          {message && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-200">
              {message}
            </div>
          )}
          
          <button 
            className="w-full rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60" 
            type="submit"
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : '💾 Sauvegarder la clé API Anthropic'}
          </button>
        </section>
      </form>
    </ConfigPageLayout>
  )
}
